import { useCallback, useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"

import { MIN_TOUCH_SPAN_PIXELS } from "helpers/utils/constants"
import {
  computeZoomRange,
  isInsideDrawingArea,
  isOutsideVertically,
  pixelSpanToWindow,
  windowToPixels,
} from "./helpers"
import type { PlotBounds, SelectionBand, SvgPoint, TimeWindow } from "./helpers"

interface TouchSpanState {
  /** Current finger distance, in pixels. */
  distance: number
  /** The time range the fingers bound, or null while they are too close together. */
  window: TimeWindow | null
}

interface UseTouchSpanZoomOptions {
  drawingArea: PlotBounds
  getSvgPoint: (e: PointerEvent) => SvgPoint | null
  commitRange: (start: DateTime, end: DateTime) => void
  xDomainMin: number
  xDomainMax: number
}

interface UseTouchSpanZoomReturn {
  /** The band the two fingers currently bound, or null when there is none. */
  selection: SelectionBand | null
  onPointerDown: (e: PointerEvent) => void
  onPointerMove: (e: PointerEvent) => void
  onPointerUp: (e: PointerEvent) => void
  onPointerCancel: (e: PointerEvent) => void
  /** Drop every tracked finger and start over. */
  abort: () => void
}

/**
 * The touch half of the zoom: two fingers span the range — they are its edges,
 * so the selection starts as the sliver between them and grows as they
 * separate. A single finger is deliberately left untouched so it keeps driving
 * the tooltip through MUI's own gestures; sharing one finger between hover and
 * zoom made the two indistinguishable.
 *
 * Fingers are implicitly captured by the element that got their pointerdown, so
 * one sliding off the chart still reaches the window listeners — no explicit
 * capture needed here.
 */
const useTouchSpanZoom = ({
  drawingArea,
  getSvgPoint,
  commitRange,
  xDomainMin,
  xDomainMax,
}: UseTouchSpanZoomOptions): UseTouchSpanZoomReturn => {
  const pointsRef = useRef(new Map<number, SvgPoint>())
  const spanRef = useRef<TouchSpanState | null>(null)
  // Set once a selection has been consumed (or invalidated by a third finger) and
  // cleared when the last finger lifts, so leftover fingers cannot start a
  // second zoom out of the tail of the same gesture.
  const blockedRef = useRef(false)
  const [spanWindow, setSpanWindow] = useState<TimeWindow | null>(null)

  const clearSpan = useCallback(() => {
    spanRef.current = null
    setSpanWindow(null)
  }, [])

  // An interrupt can swallow the pointerup, leaving phantom fingers in the map
  // and no way back. Drop everything instead of applying anything.
  const abort = useCallback(() => {
    pointsRef.current.clear()
    blockedRef.current = false
    clearSpan()
  }, [clearSpan])

  // The window the two fingers currently bound. Recomputed from live positions
  // rather than a scale factor, so the selection literally tracks the fingers.
  const currentWindow = useCallback((): TimeWindow | null => {
    const points = pointsRef.current
    if (points.size !== 2) return null

    const [a, b] = Array.from(points.values())
    return pixelSpanToWindow({
      xA: a.x,
      xB: b.x,
      drawingArea,
      domainMin: xDomainMin,
      domainMax: xDomainMax,
    })
  }, [drawingArea, xDomainMin, xDomainMax])

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      const point = getSvgPoint(e)
      if (!point) return

      // A finger outside the plot is not part of a span selection, the same rule
      // the drag applies. Without it two fingers on the axis labels clamp to the
      // same edge, and that degenerate window still commits a zoom on lift.
      if (!isInsideDrawingArea(point, drawingArea)) return

      const points = pointsRef.current
      points.set(e.pointerId, point)

      // Three fingers is not a span selection. Drop the gesture and wait for the
      // hand to lift rather than guessing which two fingers were meant.
      if (points.size > 2) {
        blockedRef.current = true
        clearSpan()
        return
      }

      if (points.size < 2 || blockedRef.current) return

      const [a, b] = Array.from(points.values())
      const distance = Math.abs(a.x - b.x)
      const initialWindow = distance >= MIN_TOUCH_SPAN_PIXELS ? currentWindow() : null

      spanRef.current = { distance, window: initialWindow }
      setSpanWindow(initialWindow)
    },
    [getSvgPoint, drawingArea, clearSpan, currentWindow],
  )

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      const points = pointsRef.current
      if (!points.has(e.pointerId)) return

      const point = getSvgPoint(e)
      if (!point) return
      points.set(e.pointerId, point)

      const span = spanRef.current
      if (!span || points.size !== 2) return

      // Same vertical escape the drag offers. Staying inert until the whole hand
      // lifts keeps a stray finger from silently resuming the selection.
      if (isOutsideVertically(point.y, drawingArea)) {
        blockedRef.current = true
        clearSpan()
        return
      }

      const [a, b] = Array.from(points.values())

      span.distance = Math.abs(a.x - b.x)
      span.window = span.distance >= MIN_TOUCH_SPAN_PIXELS ? currentWindow() : null

      setSpanWindow(span.window)
    },
    [getSvgPoint, currentWindow, drawingArea, clearSpan],
  )

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      const points = pointsRef.current
      if (!points.delete(e.pointerId)) return

      const span = spanRef.current
      if (span) {
        // Commit on the first finger to lift, then stay inert until the hand
        // leaves so the one left behind cannot start a second zoom. A window is
        // only set while the fingers are far enough apart, so whatever was
        // previewed is applied: the fingers are the edges, no movement needed.
        if (span.window) {
          const { start, end } = computeZoomRange(
            DateTime.fromMillis(span.window.min),
            DateTime.fromMillis(span.window.max),
          )
          commitRange(start, end)
        }
        blockedRef.current = true
        clearSpan()
      }

      if (points.size === 0) {
        blockedRef.current = false
      }
    },
    [commitRange, clearSpan],
  )

  // A cancel means the browser took the gesture over, typically to scroll the
  // page: same handling as a lift, minus applying anything. Only a finger we
  // track can cancel it — these listeners live on window, so a touch that began
  // elsewhere on the page must not kill the gesture.
  const onPointerCancel = useCallback(
    (e: PointerEvent) => {
      if (!pointsRef.current.has(e.pointerId)) return

      const span = spanRef.current
      if (span) span.window = null
      onPointerUp(e)
    },
    [onPointerUp],
  )

  // Drop the tracked fingers if we unmount mid-gesture. Read the map now, while
  // it is still ours.
  useEffect(() => {
    const points = pointsRef.current
    return () => {
      points.clear()
      spanRef.current = null
      blockedRef.current = false
    }
  }, [])

  return {
    selection: spanWindow
      ? // The domain cannot change while the gesture is live, so the window the
        // fingers bound and the current props agree.
        windowToPixels({
          window: spanWindow,
          drawingArea,
          domainMin: xDomainMin,
          domainMax: xDomainMax,
        })
      : null,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    abort,
  }
}

export default useTouchSpanZoom
